// The "This person" box at the foot of the person page (no board draws it: BUILD-PLAN stage 5's move
// to another email, merge, export and delete, and the owner's Staff role line). Owner and admin, with
// Delete and the staff role for an owner only (Adam, 4 Oct, answer 1). Each goes through its function
// (data-model 10.4.1) or, for the role, a direct write an owner may make; the database checks again.
import { useEffect, useState } from 'react'
import { Button, Checkbox, ConfirmPanel, Options, Select, TextInput, useAction, useAdmin, ROLE_NAMES } from '../ui/index.js'
import { THIS, ROLE, TWO_STEP, firstOf, boardDay } from './words.js'
import { listPeople } from './data.js'

function Section({ title, hint, children, id }) {
  return (
    <section className="apl-this-row" aria-labelledby={id}>
      <div className="apl-this-text">
        <h3 className="apl-this-title" id={id}>{title}</h3>
        {hint && <p className="apl-hint">{hint}</p>}
      </div>
      <div className="apl-this-body">{children}</div>
    </section>
  )
}

function MoveEmail({ person, first, onDone }) {
  const { api } = useAdmin()
  const [email, setEmail] = useState('')
  const check = useAction(body => api.move_access_email(body))
  const move = useAction(body => api.move_access_email(body))
  const checked = check.answer && check.answer.new_email
  return (
    <>
      {!checked && (
        <div className="apl-this-form">
          <TextInput aria-label={THIS.moveLabel} type="email" placeholder={THIS.moveLabel} value={email} onChange={v => { setEmail(v); check.clear() }} autoComplete="off" />
          <Button size="sm" disabled={!email.includes('@')} busy={check.busy} onClick={() => check.run({ person_id: person.id, email: email.trim(), check: true })}>{THIS.moveCheck}</Button>
        </div>
      )}
      {check.error && <p className="ad-error" role="alert">{check.error.message}</p>}
      {checked && (
        <ConfirmPanel
          text={<>{THIS.moveConfirm(first, check.answer.old_email, check.answer.new_email)}{check.answer.warning && <span className="apl-warning">{check.answer.warning}</span>}</>}
          confirmLabel={THIS.moveGo} busy={move.busy} error={move.error}
          onConfirm={async () => { const a = await move.run({ person_id: person.id, email: check.answer.new_email }); if (a) { check.clear(); setEmail(''); onDone(a.message) } }}
          onCancel={() => { check.clear(); move.clear() }} />
      )}
    </>
  )
}

function Merge({ person, onMerged }) {
  const { api } = useAdmin()
  const [q, setQ] = useState('')
  const [found, setFound] = useState({ status: 'idle', rows: [] })
  const [other, setOther] = useState(null)
  const [keep, setKeep] = useState(person.id)
  const merge = useAction(body => api.merge_people(body))
  useEffect(() => {
    if (q.trim().length < 2) { setFound({ status: 'idle', rows: [] }); return undefined }
    let alive = true
    const t = setTimeout(() => {
      listPeople(api, { search: q }, null, 8).then(
        rows => { if (alive) setFound({ status: 'ready', rows: rows.filter(r => r.person_id !== person.id && !r.role) }) },
        () => { if (alive) setFound({ status: 'ready', rows: [] }) },
      )
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [q, api, person.id])

  if (other) {
    const a = person.full_name, b = other.full_name
    const kept = keep === person.id ? a : b
    const merged = keep === person.id ? b : a
    return (
      <>
        <Options label={THIS.merge} value={keep} onChange={setKeep}
          options={[{ value: person.id, label: THIS.mergeKeep(a), note: person.email }, { value: other.person_id, label: THIS.mergeKeep(b), note: other.email }]} />
        <ConfirmPanel text={THIS.mergeConfirm(merged, kept)} confirmLabel={THIS.mergeGo} cancelLabel={THIS.mergeCancel}
          busy={merge.busy} error={merge.error}
          onConfirm={async () => {
            const answer = await merge.run({ keep_person_id: keep, merge_person_id: keep === person.id ? other.person_id : person.id })
            if (answer) onMerged(answer)
          }}
          onCancel={() => { setOther(null); merge.clear() }} />
      </>
    )
  }
  return (
    <>
      <TextInput type="search" aria-label={THIS.mergeSearch} placeholder={THIS.mergeSearch} value={q} onChange={setQ} autoComplete="off" />
      {found.status === 'ready' && !found.rows.length && <p className="apl-hint">{THIS.mergeNone}</p>}
      {found.rows.length > 0 && (
        <ul className="apl-found">
          {found.rows.map(r => (
            <li key={r.person_id} className="apl-found-row">
              <span className="apl-found-text"><span className="apl-found-name">{r.full_name}</span><span className="apl-found-email">{r.email}</span></span>
              <Button size="xs" onClick={() => { setOther(r); setKeep(person.id) }}>{THIS.mergePick}</Button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function StaffRole({ person, products, scopes, first, onDone }) {
  const { api } = useAdmin()
  const [role, setRole] = useState(person.role || '')
  const [hosts, setHosts] = useState(() => new Set(scopes))
  const save = useAction(async () => {
    if (role === 'host' && !hosts.size) throw new api.AdminError('host_needs_product', 400, ROLE.needProduct)
    if ((person.role || '') !== role) await api.set_role(person.id, role || null)
    if (role === 'host') {
      for (const id of hosts) if (!scopes.includes(id)) await api.add_scope({ person_id: person.id, product_id: id })
      for (const id of scopes) if (!hosts.has(id)) await api.remove_scope({ person_id: person.id, product_id: id })
    }
    return true
  })
  const changed = (person.role || '') !== role || (role === 'host' && (hosts.size !== scopes.length || [...hosts].some(id => !scopes.includes(id))))
  const paid = products.filter(p => p.access_kind === 'paid' && p.moved_in && p.type !== 'client_space')
  return (
    <>
      <Select aria-label={ROLE.title} value={role} onChange={setRole}
        options={[{ value: '', label: ROLE.none }, ...['owner', 'admin', 'support', 'host'].map(r => ({ value: r, label: ROLE_NAMES[r] }))]} />
      {role === 'host' && (
        <div className="apl-hosts" role="group" aria-label={ROLE.hostFor}>
          <span className="ad-label">{ROLE.hostFor}</span>
          {paid.map(p => (
            <Checkbox key={p.id} checked={hosts.has(p.id)} onChange={on => setHosts(s => { const n = new Set(s); if (on) n.add(p.id); else n.delete(p.id); return n })}>{p.name}</Checkbox>
          ))}
        </div>
      )}
      <span><Button size="sm" kind="ink" disabled={!changed} busy={save.busy} busyLabel={ROLE.busy}
        onClick={async () => { if (await save.run()) onDone(ROLE.done(first, role || null)) }}>{ROLE.save}</Button></span>
      {save.error && <p className="ad-error" role="alert">{save.error.message}</p>}
    </>
  )
}

// An owner resets another staff member's 2-step login: every session of the login ends, and nothing is
// emailed (reset-two-step, data-model 10.4.1). Never their own: that's done in Supabase's dashboard.
function ResetTwoStep({ person, first, onDone }) {
  const { api } = useAdmin()
  const [confirm, setConfirm] = useState(false)
  const reset = useAction(body => api.reset_two_step(body))
  if (!confirm) return <span><Button size="sm" onClick={() => setConfirm(true)}>{TWO_STEP.button}</Button></span>
  return (
    <ConfirmPanel text={TWO_STEP.confirm(first, person.login_shared)} confirmLabel={TWO_STEP.go} busy={reset.busy} error={reset.error}
      onConfirm={async () => { const a = await reset.run({ person_id: person.id }); if (a) { setConfirm(false); onDone(a.message) } }}
      onCancel={() => { setConfirm(false); reset.clear() }} />
  )
}

export function ThisPerson({ data, role, me, onChanged, onMerged, onDeleted }) {
  const { api } = useAdmin()
  const person = data.person
  const first = firstOf(person)
  const staff = Boolean(person.role)
  const self = me?.who?.person_id === person.id
  const exp = useAction(body => api.data_export(body))
  const del = useAction(body => api.delete_person(body))
  const [confirmDelete, setConfirmDelete] = useState(false)
  const request = data.requests.find(r => r.kind === 'export')
  return (
    <section className="ad-card apl-this" aria-labelledby="apl-this-h">
      <div className="ad-card-head"><h2 className="ad-card-title" id="apl-this-h">{THIS.title}</h2></div>
      {data.previous.length > 0 && (
        <Section id="apl-this-old" title={THIS.oldEmails}>
          <ul className="apl-old">
            {data.previous.map(p => <li key={p.id}>{THIS.oldEmail(p.email, boardDay(p.changed_at))}</li>)}
          </ul>
        </Section>
      )}
      {(!staff || role === 'owner') && !self && (
        <Section id="apl-this-move" title={THIS.move} hint={THIS.moveHint(first)}>
          <MoveEmail person={person} first={first} onDone={onChanged} />
        </Section>
      )}
      <Section id="apl-this-merge" title={THIS.merge} hint={staff ? null : THIS.mergeHint}>
        {staff ? <p className="apl-hint">{THIS.staffNo}</p> : <Merge person={person} onMerged={onMerged} />}
      </Section>
      <Section id="apl-this-export" title={THIS.export(first)} hint={request ? THIS.exportOpen(first, boardDay(request.requested_at)) : THIS.exportNone(first)}>
        {/* an open request is answered; with none, data-export opens 1 for this person (owner and admin) */}
        <span><Button size="sm" busy={exp.busy} disabled={Boolean(exp.answer)}
          onClick={async () => { const a = await exp.run(request ? { request_id: request.id } : { person_id: person.id }); if (a) onChanged(a.message) }}>{THIS.exportGo}</Button></span>
        {exp.error && <p className="ad-error" role="alert">{exp.error.message}</p>}
      </Section>
      {role === 'owner' && !staff && (
        <Section id="apl-this-delete" title={THIS.delete} hint={THIS.deleteHint(first)}>
          {person.login_shared && <p className="apl-warning">{THIS.deleteShared(first)}</p>}
          {confirmDelete
            ? <ConfirmPanel text={THIS.deleteConfirm(person.full_name)} confirmLabel={THIS.deleteGo} busy={del.busy} error={del.error}
                onConfirm={async () => { const a = await del.run({ person_id: person.id }); if (a) onDeleted(a) }}
                onCancel={() => { setConfirmDelete(false); del.clear() }} />
            : <span><Button size="sm" onClick={() => setConfirmDelete(true)}>{THIS.deleteButton(first)}</Button></span>}
        </Section>
      )}
      {role === 'owner' && staff && !self && (
        <Section id="apl-this-two-step" title={TWO_STEP.title} hint={TWO_STEP.hint(first)}>
          <ResetTwoStep person={person} first={first} onDone={onChanged} />
        </Section>
      )}
      {role === 'owner' && (
        <Section id="apl-this-role" title={ROLE.title} hint={self ? ROLE.own : ROLE.hint}>
          {!self && <StaffRole person={person} products={data.catalogue} scopes={data.scopes} first={first} onDone={onChanged} />}
        </Section>
      )}
    </section>
  )
}
